import {
  isPast, isToday, interval, isWithinInterval, parseISO, subDays,
} from 'date-fns';

export enum policyStates {
  active = 'active',
  pending = 'pending',
  cancelled = 'cancelled',
  cancellationScheduled = 'cancellation_scheduled',
  expired = 'expired',
}

const isPolicyEndDateInPast = (policyEndDate): boolean => {
  const parsedEndDate = parseISO(policyEndDate).getTime();

  return !isToday(parsedEndDate) && isPast(parsedEndDate);
};

const isPending = (policyStartDate): boolean => {
  const parsedStartDate = parseISO(policyStartDate).getTime();

  return parsedStartDate > Date.now();
};

// Returns the same date at midnight but with the client timezone
const enforceMidnight = (dateWithTime: String) => {
  // expected yyyy-mm-ddT00:00:00.000+01:00 (BST)
  // or       yyyy-mm-ddT00:00:00.000+00:00 (UTC)
  // the time and timezone from the dateWithTime does not matter
  const date = dateWithTime.match(/\d{4}-\d{2}-\d{2}/)?.[0];

  if (!date) return null;

  return new Date(date.concat('T00:00:00.000'));
};

export interface Document {
  type: string;
  key: string;
  path: string;
  fileName: string;
}

export interface Cover {
  name: string;
  displayName: string;
  displayLimit: string;
  covered?: boolean;
  limit?: { amount?: number };
  requested?: boolean;
}

interface Segment {
  documentsPending: boolean;
  filterOverridden: boolean;
  riskDetails: RiskDetails;
  documents: Document[];
  covers: Cover[];
  basisOfQuotation: BasisOfQuotation;
}

interface PolicyData {
  state: string;
  segments: Segment[];
  productDisplayNames: string[];
  allowAutoRenew: boolean;
  [key: string]: unknown; // Catch all
}

interface RiskDetails {
  businessName: string;
  address1: string;
  address2: string;
  town: string;
  postalCode: string;
  subDivision: string;
}

interface BasisOfQuotation {
  primaryTrade: string
}

type PolicyState = ValueOf<typeof policyStates>;

export default class Policy {
  segments!: Segment[];

  startDate!: Date;

  endDate!: Date;

  vertical!: string;

  category!: string;

  policyNumber!: string;

  customerAccountIdentifier!: string;

  state!: PolicyState;

  id!: string; // TODO: Check this

  productDisplayNames!: string[];

  allProductNames!: string;

  isCurrent!: boolean;

  invoices!: Document[];

  canOptOutOfAutoRenewal!: boolean;

  distributionPartner!: string;

  allowAutoRenew!: boolean;

  insurerAtExpiry!: string;

  /*
  The current "state" attribute we have in our system (Chopin)
  doesn't really reflect the actual state of the policy.
  This method is meant to help in displaying the Policy state in the front-end.
  The state values handled within this project are:
  - active
  - pending
  - expired
  - cancelled
  - cancellation_scheduled
  The rest of the front-end code must reference only this set of values.
  */

  convertState = ({
    state: rawState,
    endDate,
    startDate,
  }: PolicyData): PolicyState => {
    const state = rawState.toLowerCase();

    if (state === 'cancellation_scheduled') return policyStates.cancellationScheduled;
    if (state === 'cancelled') return policyStates.cancelled;
    if (isPending(startDate)) return policyStates.pending;

    if (state === 'expired' || isPolicyEndDateInPast(endDate)) return policyStates.expired;

    if (['active', 'renewed'].includes(state)) return policyStates.active;

    return policyStates.active;
  };

  private autoRenewalOptOutEligibility(state: string, allowAutoRenew: boolean): boolean {
    return ['active', 'cancellation_scheduled', 'pending'].includes(
      state.toLowerCase(),
    ) && allowAutoRenew;
  }

  get isSBDistributionPartner(): boolean {
    return this.distributionPartner?.toLowerCase() === 'simplybusiness';
  }

  get isUsSbDistributionPartner(): boolean {
    return this.distributionPartner?.toLowerCase() === 'simplybusiness_us';
  }

  get someDocumentsPending() {
    return this.segments.some(segment => segment.documentsPending);
  }

  get hasCancellationInvoice(): boolean {
    // A cancellation invoice can be returned at either level: attached to an
    // individual segment (segment.documents) or on the policy-level invoices
    // list (this.invoices). We combine both so we don't miss it.
    const documents = [
      ...this.segments.flatMap(segment => segment.documents ?? []),
      ...(this.invoices ?? []),
    ];

    return documents.some(document => document.fileName.toLowerCase().includes('cancellation invoice'));
  }

  get cancellationInvoicePending(): boolean {
    const isCancelling = [policyStates.cancelled, policyStates.cancellationScheduled].includes(this.state);

    return isCancelling && !this.hasCancellationInvoice;
  }

  get isWithinRebrokePeriod(): boolean {
    const twentyOneDaysFromExpiry = subDays(this.endDate, 21);
    const rebrokeInterval = interval(twentyOneDaysFromExpiry, this.endDate);

    return isWithinInterval(new Date(), rebrokeInterval);
  }

  get isActive(): boolean {
    return this.state === policyStates.active;
  }

  get isLandlord(): boolean {
    return this.vertical === 'landlord' || this.vertical === 'commercial_landlord';
  }

  get hasOptedOutOfAutoRenewal(): boolean {
    return this.allowAutoRenew === false
      && [policyStates.active, policyStates.cancellationScheduled, policyStates.pending].includes(this.state);
  }

  allProductDisplayNames = (policyData: PolicyData): string => policyData.productDisplayNames.join(', ');

  constructor(policyData) {
    const state = this.convertState(policyData);
    const allProductNames = this.allProductDisplayNames(policyData);
    const canOptOutOfAutoRenewal = this.autoRenewalOptOutEligibility(
      state, policyData.allowAutoRenew,
    );

    Object.assign(this, {
      ...policyData,
      state,
      allProductNames,
      canOptOutOfAutoRenewal,
      startDate: enforceMidnight(policyData.startDate),
      endDate: enforceMidnight(policyData.endDate),
      isCurrent: [policyStates.active, policyStates.pending, policyStates.cancellationScheduled].includes(state),
    });
  }
}
